import { useState, useEffect } from "react";
import MatchCard from "../../../../utils/tournament/matchCard";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { showToast } from "../../../../components/ToastNotification";
import ExportBracketsModal from "../../../../components/ExportBracketsModal";
import ExportChampionModal from "../../../../components/ExportChampionModal";

import teamicon from "../../../../assets/teamicon.png";

const BracketsTab = ({ tournament, selectedSubcategory }) => {
  const [teams, setTeams] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);

  // Nuevos estados (categoría por equipo)
  const [team1Subcat, setTeam1Subcat] = useState(null);
  const [team2Subcat, setTeam2Subcat] = useState(null);
  const [teamsTeam1, setTeamsTeam1] = useState([]);
  const [teamsTeam2, setTeamsTeam2] = useState([]);

  // Fases dinámicas
  const [phases, setPhases] = useState([]);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState("");

  // Crear partido
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [currentPhaseId, setCurrentPhaseId] = useState(null);
  const [newMatch, setNewMatch] = useState({
    team1_id: "",
    team2_id: "",
    match_date: "",
  });

  const [matches, setMatches] = useState([]);
  const [creatingPhaseId, setCreatingPhaseId] = useState(null);

  const [showExportModal, setShowExportModal] = useState(false);
  const [showChampionModal, setShowChampionModal] = useState(false);

  // User
  const token = useSelector((state) => state.user.token);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setUserId(decoded.id);
    }
  }, [token]);

  const isTournamentOwner = userId === tournament.user_id;

  // Subcategorías
  useEffect(() => {
    const fetchSubcats = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/brackets/${tournament.id}/subcategories`,
      );
      const data = await res.json();
      setAllSubcategories(data);
    };
    fetchSubcats();
  }, [tournament.id]);

  // Cargar equipos por subcategoría
  const loadTeamsBySubcat = async (subcategoryId, isTeam1) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams?subcategoryId=${subcategoryId}`,
    );
    const data = await res.json();
    if (isTeam1) setTeamsTeam1(data);
    else setTeamsTeam2(data);
  };

  // Carga inicial de equipos
  const loadTeams = async (subcategoryId) => {
    const url = subcategoryId
      ? `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams?subcategoryId=${subcategoryId}`
      : `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams`;

    const res = await fetch(url);
    const data = await res.json();
    setTeams(data);
  };

  useEffect(() => {
    loadTeams(selectedSubcategory.id);
  }, [selectedSubcategory]);

  // Cargar partidos
  useEffect(() => {
    const loadMatches = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/brackets/${tournament.id}/matches?subcategoryId=${selectedSubcategory.id}`,
      );
      const data = await res.json();

      if (data.length === 0) {
        setPhases([]);
        return;
      }

      const phaseMap = {};

      data.forEach((m) => {
        if (!phaseMap[m.phase_name]) phaseMap[m.phase_name] = [];
        phaseMap[m.phase_name].push({
          id: m.id,
          team1_id: m.team1_id,
          team2_id: m.team2_id,
          match_date: m.match_date,
          status: m.status,
          score_team1: m.score_team1,
          score_team2: m.score_team2,
          team1_name_db: m.home_team_name,
          team2_name_db: m.away_team_name,
          team1_logo_db: m.home_team_logo,
          team2_logo_db: m.away_team_logo,
          team1_category_id: m.team1_category_id || null,
          team2_category_id: m.team2_category_id || null,
          team1_category: m.team1_category,
          team1_group_name: m.team1_group_name,
          team2_category: m.team2_category,
          team2_group_name: m.team2_group_name,
        });
      });

      const formatted = Object.keys(phaseMap).map((phaseName) => ({
        id: Date.now() + Math.random(),
        name: phaseName,
        matches: phaseMap[phaseName],
      }));

      setPhases(formatted);
    };

    loadMatches();
  }, [selectedSubcategory.id, tournament.id]);

  // Agregar fase
  const addPhase = () => {
    if (!newPhaseName.trim()) return alert("Escribe un nombre");

    const newPhase = {
      id: Date.now(),
      name: newPhaseName,
      matches: [],
      // Ya no necesitas isNew
    };

    setPhases([...phases, newPhase]);
    setNewPhaseName("");
    setShowPhaseModal(false);
  };

  // Abrir modal partido
  const openMatchModal = (phaseId) => {
    setCurrentPhaseId(phaseId);
    setTeam1Subcat(null);
    setTeam2Subcat(null);
    setTeamsTeam1([]);
    setTeamsTeam2([]);

    // Obtener fecha y hora actual en formato datetime-local
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

    setNewMatch({
      team1_id: "",
      team2_id: "",
      match_date: currentDateTime, // ← fecha actual por defecto
    });

    setShowMatchModal(true);
  };

  // Crear partido
  const createMatch = async () => {
    const { team1_id, team2_id, match_date } = newMatch;

    if (!team1_id || !team2_id || !match_date)
      return alert("Completa todos los campos");
    if (team1_id === team2_id)
      return alert("Los equipos no pueden ser iguales");

    const phase = phases.find((p) => p.id === currentPhaseId);
    if (!phase) return alert("Fase no encontrada");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/brackets/${tournament.id}/create-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            phase: phase.name,
            team1_id,
            team2_id,
            match_date,
            subcategory_id: selectedSubcategory.id,
            team1_category_id: team1Subcat?.id || null,
            team2_category_id: team2Subcat?.id || null,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error al crear partido");
      }

      const rawMatch = await response.json();

      // 🔄 Formatear el objeto igual a como lo lee el useEffect de carga inicial
      const createdMatch = {
        id: rawMatch.id,
        team1_id: rawMatch.team1_id,
        team2_id: rawMatch.team2_id,
        match_date: rawMatch.match_date,
        status: rawMatch.status,
        score_team1: rawMatch.score_team1,
        score_team2: rawMatch.score_team2,
        team1_name_db: rawMatch.home_team_name,
        team2_name_db: rawMatch.away_team_name,
        team1_logo_db: rawMatch.home_team_logo,
        team2_logo_db: rawMatch.away_team_logo,
        team1_category_id: rawMatch.team1_category_id,
        team2_category_id: rawMatch.team2_category_id,
        team1_category: rawMatch.team1_category,
        team1_group_name: rawMatch.team1_group_name,
        team2_category: rawMatch.team2_category,
        team2_group_name: rawMatch.team2_group_name,
      };

      setPhases((prev) =>
        prev.map((p) =>
          p.id === currentPhaseId
            ? { ...p, matches: [...p.matches, createdMatch] }
            : p,
        ),
      );

      showToast.success("Partido creado correctamente");
      setShowMatchModal(false);
    } catch (error) {
      showToast.error(error.message || "Error al guardar el partido");
    }
  };

  const handleDelete = async (matchId) => {
    const confirmDelete = window.confirm(
      "¿Seguro que quieres eliminar este partido?",
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/brackets/${matchId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) throw new Error("Error al eliminar el partido");

      setMatches((prevMatches) =>
        prevMatches.filter((match) => match.id !== matchId),
      );

      showToast.success("Partido eliminado con éxito!");
    } catch (error) {
      showToast.error("Error al eliminar el partido");
    }
  };

  const handleUpdateMatch = async (updatedMatch) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matches/${updatedMatch.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: updatedMatch.status,
            score_team1: updatedMatch.score_team1,
            score_team2: updatedMatch.score_team2,
            match_date: updatedMatch.match_date,
          }),
        },
      );

      if (!response.ok) throw new Error("Error al actualizar el partido");

      const data = await response.json();

      // Actualizar el estado local
      setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match.id === updatedMatch.id ? { ...match, ...data } : match,
        ),
      );

      showToast.success("Partido actualizado con éxito!");
    } catch (error) {
      showToast.error("Error al actualizar el partido");
      throw error;
    }
  };

  // Guardar al backend
  const saveToBackend = async (phaseToSave) => {
    try {
      for (const match of phaseToSave.matches) {
        const bodyData = {
          phase: phaseToSave.name,
          team1_id: match.team1_id,
          team2_id: match.team2_id,
          match_date: match.match_date,
          subcategory_id: selectedSubcategory.id,
        };

        await fetch(
          `${process.env.REACT_APP_API_URL}/api/brackets/${tournament.id}/create-match`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(bodyData),
          },
        );
      }

      showToast.success(`Fase "${phaseToSave.name}" guardada correctamente`);
      setCreatingPhaseId(null);
    } catch (err) {
      showToast.error("Error guardando la fase");
    }
  };

  const getTeamName = (id) =>
    teams.find((t) => t.team_id === id)?.team_name || "Por definirse";
  const getTeamLogo = (id) =>
    teams.find((t) => t.team_id === id)?.picture_url || "/defaultlogo.png";

  return (
    <div className="text-white">
      {isTournamentOwner && (
        <div className="flex justify-start mb-4 gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow transition-colors"
            title="Exportar bracket como imagen"
          >
            {/* SVG export bracket... */}
            Exportar Bracket
          </button>

          {/* NUEVO BOTÓN */}
          <button
            onClick={() => setShowChampionModal(true)}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg shadow transition-colors"
            title="Exportar campeón"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            Exportar Campeón
          </button>
        </div>
      )}

      <div className="flex gap-6 overflow-auto">
        {phases.map((phase) => (
          <div key={phase.id} className="min-w-[260px]">
            <h3 className="text-md font-semibold mb-2 text-center border-b border-gray-600 pb-1">
              {phase.name}
            </h3>
            {phase.matches.map((match) => (
              <MatchCard
                key={match.id}
                onDelete={isTournamentOwner ? handleDelete : null}
                onEdit={isTournamentOwner ? handleUpdateMatch : null}
                selectedSubcategory={selectedSubcategory}
                match={{
                  id: match.id,
                  team1_id: match.team1_id,
                  team2_id: match.team2_id,
                  team1_category_id: match.team1_category_id || null,
                  team2_category_id: match.team2_category_id || null,
                  team1_info: {
                    team_name:
                      match.team1_name_db || getTeamName(match.team1_id),
                    picture_url:
                      match.team1_logo_db ||
                      getTeamLogo(match.team1_id) ||
                      teamicon,
                    category: match.team1_category,
                    group_name: match.team1_group_name,
                  },
                  team2_info: {
                    team_name:
                      match.team2_name_db || getTeamName(match.team2_id),
                    picture_url:
                      match.team2_logo_db ||
                      getTeamLogo(match.team2_id) ||
                      teamicon,
                    category: match.team2_category,
                    group_name: match.team2_group_name,
                  },
                  match_date: match.match_date,
                  status: match.status,
                  score_team1: match.score_team1,
                  score_team2: match.score_team2,
                }}
                isOwner={isTournamentOwner}
              />
            ))}
            {isTournamentOwner && (
              <button
                className="w-full bg-purple-600 py-2 rounded mt-3 hover:bg-purple-700"
                onClick={() => openMatchModal(phase.id)}
              >
                + Agregar Partido
              </button>
            )}
          </div>
        ))}

        {isTournamentOwner && (
          <button
            className="min-w-[260px] h-[60px] bg-blue-600 py-3 px-4 rounded hover:bg-blue-700"
            onClick={() => setShowPhaseModal(true)}
          >
            + Agregar Fase
          </button>
        )}
      </div>

      <ExportBracketsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        phases={phases}
        teams={teams}
        subcategory={selectedSubcategory}
      />
      <ExportChampionModal
        isOpen={showChampionModal}
        onClose={() => setShowChampionModal(false)}
        allSubcategories={allSubcategories}
        tournamentId={tournament.id}
        token={token}
      />
      {/* Modal Nueva Fase */}
      {showPhaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Crear Nueva Fase</h3>
            <input
              type="text"
              className="w-full p-2 rounded bg-gray-700 text-white"
              placeholder="Nombre de la fase"
              value={newPhaseName}
              onChange={(e) => setNewPhaseName(e.target.value)}
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-600 rounded"
                onClick={() => setShowPhaseModal(false)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 bg-blue-600 rounded"
                onClick={addPhase}
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Partido */}
      {showMatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Crear Partido</h3>

            {/* Categoría equipo local */}
            <label>Categoría del equipo local</label>
            <select
              className="w-full p-2 rounded bg-gray-700 text-white mb-4"
              value={team1Subcat?.id || ""}
              onChange={(e) => {
                const sub = allSubcategories.find(
                  (s) => s.id === Number(e.target.value),
                );
                setTeam1Subcat(sub);
                setNewMatch({ ...newMatch, team1_id: "" });
                loadTeamsBySubcat(sub.id, true);
              }}
            >
              <option value="">Seleccionar...</option>
              {allSubcategories.map((s) => (
                <option key={s.id} value={s.id}>
                  {`Sub ${s.subcategory_number} - ${s.group_name}`}
                </option>
              ))}
            </select>

            {/* Equipo local */}
            <select
              className="w-full p-2 rounded bg-gray-700 text-white mb-4"
              value={newMatch.team1_id}
              disabled={!team1Subcat}
              onChange={(e) =>
                setNewMatch({ ...newMatch, team1_id: e.target.value })
              }
            >
              {teamsTeam1.map((t) => (
                <option key={t.team_id} value={t.team_id}>
                  {t.team_name} - Sub {team1Subcat.subcategory_number} (
                  {team1Subcat.group_name})
                </option>
              ))}
            </select>

            {/* Categoría equipo visitante */}
            <label>Categoría del equipo visitante</label>
            <select
              className="w-full p-2 rounded bg-gray-700 text-white mb-4"
              value={team2Subcat?.id || ""}
              onChange={(e) => {
                const sub = allSubcategories.find(
                  (s) => s.id === Number(e.target.value),
                );
                setTeam2Subcat(sub);
                setNewMatch({ ...newMatch, team2_id: "" });
                loadTeamsBySubcat(sub.id, false);
              }}
            >
              <option value="">Seleccionar...</option>
              {allSubcategories.map((s) => (
                <option key={s.id} value={s.id}>
                  {`Sub ${s.subcategory_number} - ${s.group_name}`}
                </option>
              ))}
            </select>

            {/* Equipo visitante */}
            <select
              className="w-full p-2 rounded bg-gray-700 text-white mb-4"
              value={newMatch.team2_id}
              disabled={!team2Subcat}
              onChange={(e) =>
                setNewMatch({ ...newMatch, team2_id: e.target.value })
              }
            >
              {teamsTeam2.map((t) => (
                <option key={t.team_id} value={t.team_id}>
                  {t.team_name} - Sub {team2Subcat.subcategory_number} (
                  {team2Subcat.group_name})
                </option>
              ))}
            </select>

            {/* Fecha */}
            <input
              type="datetime-local"
              className="w-full p-2 rounded bg-gray-700 text-white mb-4"
              value={newMatch.match_date}
              onChange={(e) =>
                setNewMatch({ ...newMatch, match_date: e.target.value })
              }
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-600 rounded"
                onClick={() => setShowMatchModal(false)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 bg-purple-600 rounded"
                onClick={createMatch}
              >
                Crear Partido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketsTab;
